import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api, ApiError } from "../api/client.ts";
import type { JobInput } from "@shared/types";

// ---------------------------------------------------------------------------
// Single-form intake (PRD §7.1 / Appendix A). One screen, one card: capture the
// minimum a tech needs to type, then hand off to orchestration.
// ---------------------------------------------------------------------------

interface FormState {
  customer_name: string;
  address: string;
  phone: string;
  email: string;
  removal_at: string; // value of <input type="datetime-local">
  invoice_amount: string; // kept as string for the number input
  notes: string;
}

type FieldErrors = Partial<Record<keyof FormState, string>>;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EMPTY: FormState = {
  customer_name: "",
  address: "",
  phone: "",
  email: "",
  removal_at: "",
  invoice_amount: "",
  notes: "",
};

function validate(form: FormState): FieldErrors {
  const errors: FieldErrors = {};

  if (!form.customer_name.trim()) errors.customer_name = "Customer name is required.";
  if (!form.address.trim()) errors.address = "Address is required.";
  if (!form.phone.trim()) errors.phone = "Phone is required.";

  if (!form.email.trim()) {
    errors.email = "Email is required.";
  } else if (!EMAIL_RE.test(form.email.trim())) {
    errors.email = "Enter a valid email address.";
  }

  if (!form.removal_at) {
    errors.removal_at = "Removal date & time is required.";
  } else {
    const d = new Date(form.removal_at);
    if (Number.isNaN(d.getTime())) {
      errors.removal_at = "Enter a valid date & time.";
    }
  }

  if (form.invoice_amount.trim()) {
    const amount = Number(form.invoice_amount);
    if (!Number.isFinite(amount) || amount < 0) {
      errors.invoice_amount = "Enter a valid amount.";
    }
  }

  return errors;
}

export function Intake() {
  const navigate = useNavigate();

  // One idempotency key per form mount makes an accidental double-submit safe.
  const idempotencyKey = useMemo(() => crypto.randomUUID(), []);

  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
    // Clear a field's error as the user edits it.
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);

    const nextErrors = validate(form);
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    const amount = form.invoice_amount.trim();
    const payload: JobInput = {
      customer_name: form.customer_name.trim(),
      address: form.address.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      removal_at: new Date(form.removal_at).toISOString(),
      invoice_amount: amount ? Number(amount) : null,
      idempotency_key: idempotencyKey,
    };
    const notes = form.notes.trim();
    if (notes) payload.notes = notes;

    setSubmitting(true);
    try {
      const job = await api.createJob(payload);
      navigate(`/jobs/${job.id}`);
    } catch (err) {
      if (err instanceof ApiError) {
        setFormError(err.message);
        if (err.details && typeof err.details === "object") {
          // Surface any server-side field errors inline alongside our own.
          const detailErrors: FieldErrors = {};
          for (const [key, value] of Object.entries(err.details as Record<string, unknown>)) {
            if (key in EMPTY && typeof value === "string") {
              detailErrors[key as keyof FormState] = value;
            }
          }
          if (Object.keys(detailErrors).length > 0) {
            setErrors((prev) => ({ ...prev, ...detailErrors }));
          }
        }
      } else {
        setFormError(err instanceof Error ? err.message : "Something went wrong.");
      }
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-slate-900">New R&amp;R Job</h1>
        <p className="mt-1 text-sm text-slate-500">
          Enter the job details once. We&rsquo;ll create the sheet row, agreement, invoice,
          calendar events, and removal-photos folder for you.
        </p>
      </div>

      <form onSubmit={handleSubmit} noValidate className="card p-6">
        {formError && (
          <div className="mb-5 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {formError}
          </div>
        )}

        <div className="space-y-5">
          <Field label="Customer name" htmlFor="customer_name" error={errors.customer_name}>
            <input
              id="customer_name"
              type="text"
              autoComplete="name"
              className="input"
              value={form.customer_name}
              onChange={(e) => update("customer_name", e.target.value)}
              aria-invalid={errors.customer_name ? true : undefined}
            />
          </Field>

          <Field label="Address" htmlFor="address" error={errors.address}>
            <input
              id="address"
              type="text"
              autoComplete="street-address"
              className="input"
              value={form.address}
              onChange={(e) => update("address", e.target.value)}
              aria-invalid={errors.address ? true : undefined}
            />
          </Field>

          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Phone" htmlFor="phone" error={errors.phone}>
              <input
                id="phone"
                type="tel"
                autoComplete="tel"
                className="input"
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                aria-invalid={errors.phone ? true : undefined}
              />
            </Field>

            <Field label="Email" htmlFor="email" error={errors.email}>
              <input
                id="email"
                type="email"
                autoComplete="email"
                className="input"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                aria-invalid={errors.email ? true : undefined}
              />
            </Field>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Removal date & time" htmlFor="removal_at" error={errors.removal_at}>
              <input
                id="removal_at"
                type="datetime-local"
                className="input"
                value={form.removal_at}
                onChange={(e) => update("removal_at", e.target.value)}
                aria-invalid={errors.removal_at ? true : undefined}
              />
            </Field>

            <Field
              label="Invoice amount"
              htmlFor="invoice_amount"
              optional
              error={errors.invoice_amount}
            >
              <div className="relative">
                <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-slate-400">
                  $
                </span>
                <input
                  id="invoice_amount"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  className="input pl-7"
                  value={form.invoice_amount}
                  onChange={(e) => update("invoice_amount", e.target.value)}
                  aria-invalid={errors.invoice_amount ? true : undefined}
                />
              </div>
            </Field>
          </div>

          <Field label="Notes" htmlFor="notes" optional error={errors.notes}>
            <textarea
              id="notes"
              rows={4}
              className="input resize-y"
              placeholder="Anything the crew or the docs should know."
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
            />
          </Field>
        </div>

        <div className="mt-6 flex items-center justify-end gap-3">
          <button type="submit" className="btn-primary" disabled={submitting}>
            {submitting ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Processing…
              </>
            ) : (
              "Create job"
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({
  label,
  htmlFor,
  error,
  optional,
  children,
}: {
  label: string;
  htmlFor: string;
  error?: string;
  optional?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label htmlFor={htmlFor} className="label">
        {label}
        {optional && <span className="ml-1 font-normal text-slate-400">(optional)</span>}
      </label>
      {children}
      {error && <p className="mt-1 text-xs text-rose-600">{error}</p>}
    </div>
  );
}
